import {
  RUNTIME_HEARTBEAT_INTERVAL_MS,
  RUNTIME_READY_MESSAGE_TYPE,
  RUNTIME_STABLE_MESSAGE_TYPE,
  RUNTIME_ROUTE_NOT_FOUND_MESSAGE_TYPE,
  RUNTIME_ERROR_MESSAGE_TYPE,
  RUNTIME_HEARTBEAT_MESSAGE_TYPE,
  injectRuntimeReadySignal,
} from "@/lib/operatingSystemRuntimeReady";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import {
  OperatingSystemPreviewDescriptor,
  getStoredOperatingSystemPreviewSnapshot,
  rememberOperatingSystemPreviewSnapshot,
} from "@/lib/operating-systems";
import {
  OPERATING_SYSTEM_PREVIEW_LOADED_EVENT,
  OPERATING_SYSTEM_PREVIEW_PROVIDER_LOADING_EVENT,
  OperatingSystemPreviewCacheContext,
  type OperatingSystemPreviewLoadedDetail,
  type OperatingSystemPreviewProviderLoadingDetail,
  type OperatingSystemPreviewCacheValue,
  type OperatingSystemPreviewReloadOptions,
} from "@/components/operating-systems/OperatingSystemPreviewCacheContext";
import {
  injectOperatingSystemRuntimeStorageSeed,
  parseOperatingSystemRuntimeStorageMessage,
  persistOperatingSystemRuntimeStorage,
  readOperatingSystemRuntimeStorageSeed,
} from "@/lib/operatingSystemRuntimeStorage";
import {
  activePreviewCacheKeyAfterCandidatePromotion,
  canonicalPreviewCacheKey,
  createCandidateCachedPreviewEntry,
  createNextCachedPreviewEntry,
  buildOperatingSystemRuntimeLifecycleEvent,
  findExistingPreviewEntry,
  isPendingPreviewCandidate,
  isActivePreviewEntryLoaded,
  isActivePreviewFrameVisible,
  isPreviewEntrySupersededByDescriptor,
  operatingSystemRuntimeLifecycleEventKey,
  previewEntryBelongsToCacheKey,
  previewEntrySharesFamilyWithEntryKey,
  hidePreviewEntryAfterRuntimeError,
  recoverPreviewEntryAfterHeartbeat,
  recoverPreviewEntryAfterRuntimeError,
  rememberPreviewSrcDoc,
  refreshPreviewEntryDescriptorMetadata,
  runtimeStartupErrorRetryDelay,
  shouldRecoverAfterRuntimeHeartbeatBlank,
  shouldRenderPreviewFrame,
  shouldRevealPreviewFrame,
  shouldPromotePreviewCandidate,
  shouldKeepInFlightPreview,
  type OperatingSystemCachedPreviewEntry,
  type OperatingSystemRuntimeLifecycleClientPhase,
  type OperatingSystemRuntimeLifecycleClientReason,
  type OperatingSystemRuntimeLifecycleClientSignal,
} from "@/lib/operatingSystemPreviewCache";
import {
  hardenOperatingSystemRuntimeHtml,
  isRuntimeBootstrapAssetReady,
  operatingSystemRuntimeFallbackImports,
  operatingSystemRuntimeProxyBaseUrl,
  runtimeBootstrapAssetUrls,
} from "@/lib/operatingSystemRuntimeEmbed";
import {
  injectRuntimeNavigationSeed,
  persistRuntimeNavigation,
  readRuntimeNavigation,
  runtimeInternalRoute,
  RUNTIME_NAVIGATION_MESSAGE,
  RUNTIME_NAVIGATE_MESSAGE,
} from "@/lib/operatingSystemRuntimeNavigation";
import { supabase } from "@/integrations/supabase/client";

const MAX_CACHED_PREVIEWS = 3;
const CACHED_PREVIEW_TTL_MS = 30 * 60 * 1000;
const PRUNE_INTERVAL_MS = 60 * 1000;
const PREVIEW_IFRAME_Z_INDEX = 5;
const PREVIEW_LOAD_CONFIRM_DELAY_MS = 250;
const PREVIEW_HTML_REFETCH_MS = 5000;
const PREVIEW_BOOTSTRAP_PROBE_TIMEOUT_MS = 6000;
const PREVIEW_ROUTE_NOT_FOUND_MAX_RELOADS = 3;
const PREVIEW_RUNTIME_ERROR_MAX_RELOADS = 2;
const RUNTIME_HEARTBEAT_TIMEOUT_MS = RUNTIME_HEARTBEAT_INTERVAL_MS * 5;
const RUNTIME_HEARTBEAT_BLANK_SAMPLES = 3;
const RUNTIME_HEARTBEAT_BLANK_GRACE_MS = RUNTIME_HEARTBEAT_INTERVAL_MS * 2;
const RUNTIME_HEARTBEAT_TIMEOUT_MISSES = 2;
const EMPTY_PREVIEW_DOCUMENT = "<!doctype html><html><head><meta charset=\"utf-8\"></head><body></body></html>";
const PREVIEW_LOADING_SHELL_PATTERN = /Preview is (?:loading|starting up)|Preview Loading - (?:Base44|Provider)|app preview is being prepared|automatically refresh in a few seconds/i;
const META_REFRESH_PATTERN = /<meta\b[^>]*http-equiv\s*=\s*(["'])refresh\1[^>]*>/gi;
type CachedPreviewEntry = OperatingSystemCachedPreviewEntry;

const RUNTIME_LIFECYCLE_PHASES = new Set<OperatingSystemRuntimeLifecycleClientPhase>([
  "before_ready",
  "after_ready",
  "resource_error",
  "script_error",
  "unhandled_rejection",
]);

type ActivePreview = {
  cacheKey: string;
  target: HTMLElement;
  activationId: string;
};

type PreviewRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

function prunePreviewEntries(
  entries: CachedPreviewEntry[],
  activeKey: string | null,
  keepAliveKey: string | null,
): CachedPreviewEntry[] {
  const now = Date.now();
  const isExpired = (entry: CachedPreviewEntry) => {
    if (!entry.proxyExpiresAt) return false;
    const expiresAt = new Date(entry.proxyExpiresAt).getTime();
    return Number.isFinite(expiresAt) && expiresAt <= now;
  };
  const isPinnedKey = (entry: CachedPreviewEntry) => (
    previewEntrySharesFamilyWithEntryKey(entries, entry, activeKey)
    || previewEntrySharesFamilyWithEntryKey(entries, entry, keepAliveKey)
  );
  const shouldKeepExpiredLoadedEntry = (entry: CachedPreviewEntry) => (
    shouldRevealPreviewFrame(entry) && isPinnedKey(entry)
  );
  const freshEntries = entries.filter((entry) => (
    shouldKeepExpiredLoadedEntry(entry) ||
    (!isExpired(entry) && (
      isPinnedKey(entry) ||
      now - entry.lastUsedAt <= CACHED_PREVIEW_TTL_MS
    ))
  ));
  const sortedEntries = [...freshEntries].sort((first, second) => second.lastUsedAt - first.lastUsedAt);
  const pinnedEntries = sortedEntries.filter(isPinnedKey);
  const inactiveEntries = sortedEntries.filter((entry) => !isPinnedKey(entry));
  return [...pinnedEntries, ...inactiveEntries.slice(0, Math.max(0, MAX_CACHED_PREVIEWS - pinnedEntries.length))];
}

function getHiddenIframeStyle(rect: PreviewRect | null): CSSProperties {
  return {
    position: "fixed",
    left: -10000,
    top: 0,
    // Scrollbars can consume a 1px viewport and leave the App with zero
    // document width. Keep a real viewport while waiting for its ready signal.
    width: rect && rect.width > 0 ? rect.width : 1280,
    height: rect && rect.height > 0 ? rect.height : 800,
    border: 0,
    opacity: 0,
    pointerEvents: "none",
    background: "white",
  };
}

function getActiveIframeStyle(rect: PreviewRect): CSSProperties {
  return {
    position: "fixed",
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
    border: 0,
    opacity: 1,
    pointerEvents: "auto",
    background: "white",
    zIndex: PREVIEW_IFRAME_Z_INDEX,
  };
}


async function normalizePreviewSrcDoc(html: string, operatingSystemId: string, runtimeUrl: string): Promise<string> {
  const withoutRefresh = html.replace(META_REFRESH_PATTERN, "");
  const storageSeed = readOperatingSystemRuntimeStorageSeed(operatingSystemId);
  const fallbackImports = await operatingSystemRuntimeFallbackImports(withoutRefresh, runtimeUrl);
  const hardened = hardenOperatingSystemRuntimeHtml(withoutRefresh, runtimeUrl, storageSeed, fallbackImports, readRuntimeNavigation(operatingSystemId));
  return injectRuntimeReadySignal(injectOperatingSystemRuntimeStorageSeed(
    hardened,
    storageSeed,
  ));
}

class RuntimeProxyHttpError extends Error {
  constructor(public readonly status: number) {
    super(`Runtime proxy returned HTTP ${status}`);
    this.name = "RuntimeProxyHttpError";
  }
}

function runtimeProxyFailureReason(status: number | null): "ticket_invalid" | "proxy_http_error" | "runtime_proxy_fetch_failed" {
  if (status === 401 || status === 403 || status === 410) return "ticket_invalid";
  if (status !== null) return "proxy_http_error";
  return "runtime_proxy_fetch_failed";
}

function errorMessage(error: unknown): string {
  return (error instanceof Error ? error.message : String(error)).slice(0, 240);
}

function logRuntimeProxyFetchFailure(
  entry: Pick<CachedPreviewEntry, "cacheKey" | "osId" | "runtimeSource">,
  error: unknown,
) {
  const status = error instanceof RuntimeProxyHttpError ? error.status : null;
  console.warn(`[os-runtime-preview-proxy] ${JSON.stringify({
    event: "os_runtime_preview_proxy_fetch_failed",
    at: new Date().toISOString(),
    cacheKey: entry.cacheKey,
    httpStatus: status,
    osId: entry.osId,
    reason: runtimeProxyFailureReason(status),
    runtimeSource: entry.runtimeSource,
    message: errorMessage(error),
  })}`);
}

function logRuntimePreviewCacheEvent(
  details: {
    event:
      | "os_runtime_preview_preserved_visible_frame"
      | "candidate_runtime_rejected"
      | "candidate_runtime_promoted"
      | "runtime_preview_error_detected"
      | "runtime_preview_hidden_after_error"
      | "runtime_heartbeat_recovery";
    cacheKey: string;
    osId: string;
    reason:
      | "proxy_url_replaced"
      | "provider_loading_after_loaded"
      | "route_not_found_after_loaded"
      | "provider_loading"
      | "route_not_found"
      | "runtime_ready"
      | "runtime_error"
      | "runtime_error_after_loaded"
      | "runtime_heartbeat_blank"
      | "runtime_heartbeat_timeout";
    runtimeSource: "cache" | "app";
    errorPhase?: string;
    resourceType?: string;
    resourceExtension?: string;
    sameOrigin?: boolean | null;
    errorName?: string;
    line?: number;
    column?: number;
  },
) {
  console.warn(`[os-runtime-preview-cache] ${JSON.stringify({
    at: new Date().toISOString(),
    ...details,
  })}`);
}

function normalizeRuntimeErrorPhase(
  value: unknown,
  fallback: OperatingSystemRuntimeLifecycleClientPhase,
): OperatingSystemRuntimeLifecycleClientPhase {
  return typeof value === "string" && RUNTIME_LIFECYCLE_PHASES.has(value as OperatingSystemRuntimeLifecycleClientPhase)
    ? value as OperatingSystemRuntimeLifecycleClientPhase
    : fallback;
}

function RuntimePreviewFrame({
  entry,
  active,
  activeRect,
  onFrameRef,
  onProviderLoadingState,
  onNormalizedSrcDoc,
  onRuntimeUnavailable,
}: {
  entry: CachedPreviewEntry;
  active: boolean;
  activeRect: PreviewRect | null;
  onFrameRef: (cacheKey: string, node: HTMLIFrameElement | null) => void;
  onProviderLoadingState: (cacheKey: string, loading: boolean) => void;
  onNormalizedSrcDoc: (cacheKey: string, srcDoc: string) => void;
  onRuntimeUnavailable: (cacheKey: string, url: string, reloadNonce: number, unavailable: boolean) => void;
}) {
  const [frameHtml, setFrameHtml] = useState<string | null>(() => entry.lastSuccessfulSrcDoc
    ? injectRuntimeNavigationSeed(entry.lastSuccessfulSrcDoc, readRuntimeNavigation(entry.osId))
    : null);
  const [navigationTarget, setNavigationTarget] = useState<string | null>(() => readRuntimeNavigation(entry.osId)?.documentRoute || null);
  const ownFrameRef = useRef<HTMLIFrameElement | null>(null);
  const [providerLoading, setProviderLoading] = useState(false);
  const loadedAtRef = useRef(entry.loadedAt);
  const runtimeSourceRef = useRef(entry.runtimeSource);
  loadedAtRef.current = entry.loadedAt;
  runtimeSourceRef.current = entry.runtimeSource;

  useEffect(() => {
    const handleNavigation = (event: MessageEvent) => {
      if (event.source !== ownFrameRef.current?.contentWindow || event.data?.type !== RUNTIME_NAVIGATE_MESSAGE) return;
      const proxyBase = operatingSystemRuntimeProxyBaseUrl(entry.url);
      const route = event.data.route;
      if (!proxyBase || typeof route !== "string" || runtimeInternalRoute(route, proxyBase) !== route) return;
      // Non-router documents use the same ticket-scoped loading path, never a
      // browser navigation to the proxy API's text response or project root.
      setNavigationTarget(route);
    };
    window.addEventListener("message", handleNavigation);
    return () => window.removeEventListener("message", handleNavigation);
  }, [entry.url]);

  useEffect(() => {
    let cancelled = false;
    let retryTimer: number | null = null;
    let requestInFlight = false;
    let bootstrapController: AbortController | null = null;

    const clearRetryTimer = () => {
      if (retryTimer === null) return;
      window.clearTimeout(retryTimer);
      retryTimer = null;
    };

    const scheduleRetry = () => {
      clearRetryTimer();
      retryTimer = window.setTimeout(() => {
        retryTimer = null;
        void loadRuntimeHtml();
      }, document.visibilityState === "hidden" || !navigator.onLine ? 30_000 : PREVIEW_HTML_REFETCH_MS);
    };

    const loadRuntimeHtml = async () => {
      if (cancelled || requestInFlight) return;
      if (document.visibilityState === "hidden" || !navigator.onLine) {
        scheduleRetry();
        return;
      }
      requestInFlight = true;
      try {
        const route = navigationTarget;
        const proxyBase = operatingSystemRuntimeProxyBaseUrl(entry.url);
        const requestUrl = proxyBase && route && runtimeInternalRoute(route, proxyBase) === route ? `${proxyBase}${route}` : entry.url;
        const response = await fetch(requestUrl, { cache: "no-store" });
        if (cancelled) return;
        onRuntimeUnavailable(entry.cacheKey, entry.url, entry.reloadNonce,
          response.status === 404 || response.status === 412 || response.status === 502);
        const preserveVisibleFrame = !!loadedAtRef.current;
        if (response.status === 202) {
          setProviderLoading(true);
          onProviderLoadingState(entry.cacheKey, true);
          if (!preserveVisibleFrame) setFrameHtml(null);
          scheduleRetry();
          return;
        }
        if (!response.ok) {
          throw new RuntimeProxyHttpError(response.status);
        }

        const html = await response.text();
        if (cancelled) return;

        const loadingShell = PREVIEW_LOADING_SHELL_PATTERN.test(html);
        setProviderLoading(loadingShell);
        onProviderLoadingState(entry.cacheKey, loadingShell);
        if (loadingShell) {
          if (!preserveVisibleFrame) setFrameHtml(null);
          scheduleRetry();
          return;
        }
        const startupAssets = runtimeBootstrapAssetUrls(html, entry.url);
        if (startupAssets.length > 0) {
          const controller = new AbortController();
          bootstrapController = controller;
          const timeout = window.setTimeout(() => controller.abort(), PREVIEW_BOOTSTRAP_PROBE_TIMEOUT_MS);
          try {
            const responses = await Promise.all(startupAssets.map((url) => fetch(url, {
              cache: "no-store", signal: controller.signal,
            })));
            if (cancelled) return;
            if (responses.some((asset, index) => !isRuntimeBootstrapAssetReady(startupAssets[index], asset))) {
              setProviderLoading(true);
              onProviderLoadingState(entry.cacheKey, true);
              if (!preserveVisibleFrame) setFrameHtml(null);
              scheduleRetry();
              return;
            }
          } finally {
            bootstrapController = null;
            window.clearTimeout(timeout);
          }
        }
        if (cancelled) return;
        const nextFrameHtml = await normalizePreviewSrcDoc(html || EMPTY_PREVIEW_DOCUMENT, entry.osId, entry.url);
        if (cancelled) return;
        setFrameHtml(nextFrameHtml);
        onNormalizedSrcDoc(entry.cacheKey, nextFrameHtml);
      } catch (error) {
        if (cancelled) return;
        logRuntimeProxyFetchFailure({
          cacheKey: entry.cacheKey,
          osId: entry.osId,
          runtimeSource: runtimeSourceRef.current,
        }, error);
        console.warn("Failed to load App runtime proxy HTML:", error);
        setProviderLoading(true);
        onProviderLoadingState(entry.cacheKey, true);
        if (!loadedAtRef.current) setFrameHtml(null);
        scheduleRetry();
      } finally {
        requestInFlight = false;
      }
    };

    setProviderLoading(false);
    if (!loadedAtRef.current) setFrameHtml(null);
    void loadRuntimeHtml();
    const resume = () => {
      if (document.visibilityState !== "hidden" && navigator.onLine) {
        clearRetryTimer();
        void loadRuntimeHtml();
      }
    };
    window.addEventListener("online", resume);
    document.addEventListener("visibilitychange", resume);

    return () => {
      cancelled = true;
      bootstrapController?.abort();
      clearRetryTimer();
      window.removeEventListener("online", resume);
      document.removeEventListener("visibilitychange", resume);
    };
  }, [entry.cacheKey, entry.osId, entry.reloadNonce, entry.url, navigationTarget, onNormalizedSrcDoc, onProviderLoadingState, onRuntimeUnavailable]);

  // Mount the first document with App HTML already available. Replacing a
  // placeholder srcdoc during its initial navigation can leave Chrome with
  // content but no layout tree, so the runtime never becomes ready.
  if (frameHtml === null) return null;

  return (
    <iframe
      title={entry.name}
      srcDoc={frameHtml}
      aria-hidden={!active}
      tabIndex={active ? 0 : -1}
      sandbox="allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox allow-scripts allow-downloads"
      style={active && activeRect ? getActiveIframeStyle(activeRect) : getHiddenIframeStyle(activeRect)}
      ref={(node) => { ownFrameRef.current = node; onFrameRef(entry.cacheKey, node); }}
      data-provider-loading={providerLoading ? "true" : undefined}
    />
  );
}

export function OperatingSystemPreviewCacheProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<CachedPreviewEntry[]>([]);
  const entriesRef = useRef<CachedPreviewEntry[]>(entries);
  entriesRef.current = entries;
  const [activePreview, setActivePreview] = useState<ActivePreview | null>(null);
  const [activeRect, setActiveRect] = useState<PreviewRect | null>(null);
  const [keepAliveKey, setKeepAliveKey] = useState<string | null>(null);
  const activeKeyRef = useRef<string | null>(null);
  const keepAliveKeyRef = useRef<string | null>(null);
  const refreshTimersRef = useRef<Map<string, number>>(new Map());
  const startupSeenAtRef = useRef<Map<string, number>>(new Map());
  const loadConfirmTimersRef = useRef<Map<string, number>>(new Map());
  const providerLoadingKeysRef = useRef<Set<string>>(new Set());
  const [providerLoadingKeys, setProviderLoadingKeys] = useState<Set<string>>(() => new Set());
  const [runtimeBlankKeys, setRuntimeBlankKeys] = useState<Set<string>>(() => new Set());
  const [unavailableFrames, setUnavailableFrames] = useState<Map<string, { url: string; reloadNonce: number }>>(() => new Map());
  const routeNotFoundCountsRef = useRef<Map<string, number>>(new Map());
  const runtimeErrorCountsRef = useRef<Map<string, number>>(new Map());
  const runtimeErrorRetryTimersRef = useRef<Map<string, number>>(new Map());
  const runtimeHeartbeatAtRef = useRef<Map<string, number>>(new Map());
  const runtimeHeartbeatRecoveryCountsRef = useRef<Map<string, number>>(new Map());
  const runtimeHeartbeatBlankCountsRef = useRef<Map<string, number>>(new Map());
  const runtimeHeartbeatBlankSinceRef = useRef<Map<string, number>>(new Map());
  const runtimeHeartbeatTimeoutCountsRef = useRef<Map<string, number>>(new Map());
  const iframeRefsRef = useRef<Map<string, HTMLIFrameElement>>(new Map());
  const reportedLifecycleEventsRef = useRef<Set<string>>(new Set());

  const reportRuntimeLifecycleEvent = useCallback((
    entry: Pick<CachedPreviewEntry, "cacheKey" | "candidateForCacheKey" | "osId" | "runtimeSource">,
    signal: OperatingSystemRuntimeLifecycleClientSignal,
    reason: OperatingSystemRuntimeLifecycleClientReason,
    phase?: OperatingSystemRuntimeLifecycleClientPhase | null,
  ) => {
    const payload = buildOperatingSystemRuntimeLifecycleEvent(entry, signal, reason, phase);
    const eventKey = operatingSystemRuntimeLifecycleEventKey(payload);
    if (reportedLifecycleEventsRef.current.has(eventKey)) return;
    reportedLifecycleEventsRef.current.add(eventKey);

    void supabase.functions.invoke("os-runtime-lifecycle-event", { body: payload }).then(({ error }) => {
      if (error) {
        reportedLifecycleEventsRef.current.delete(eventKey);
        console.warn("Failed to report App runtime lifecycle event:", error.message);
      }
    }).catch((error) => {
      reportedLifecycleEventsRef.current.delete(eventKey);
      console.warn("Failed to report App runtime lifecycle event:", error);
    });
  }, []);

  const allowRecoveredReadyEvent = useCallback((entry: CachedPreviewEntry) => {
    reportedLifecycleEventsRef.current.delete(operatingSystemRuntimeLifecycleEventKey(
      buildOperatingSystemRuntimeLifecycleEvent(entry, "runtime_loaded", "runtime_ready"),
    ));
  }, []);

  const setProviderLoadingKey = useCallback((cacheKey: string, loading: boolean) => {
    if (loading) {
      providerLoadingKeysRef.current.add(cacheKey);
    } else {
      providerLoadingKeysRef.current.delete(cacheKey);
    }

    setProviderLoadingKeys((current) => {
      if (current.has(cacheKey) === loading) return current;
      const next = new Set(current);
      if (loading) {
        next.add(cacheKey);
      } else {
        next.delete(cacheKey);
      }
      return next;
    });
  }, []);

  const rememberNormalizedSrcDoc = useCallback((cacheKey: string, srcDoc: string) => {
    setEntries((currentEntries) => currentEntries.map((entry) => (
      entry.cacheKey === cacheKey ? rememberPreviewSrcDoc(entry, srcDoc) : entry
    )));
  }, []);

  const handleRuntimeUnavailable = useCallback((cacheKey: string, url: string, reloadNonce: number, unavailable: boolean) => {
    setUnavailableFrames((current) => {
      const existing = current.get(cacheKey);
      if ((unavailable && existing?.url === url && existing.reloadNonce === reloadNonce) || (!unavailable && !existing)) return current;
      const next = new Map(current);
      if (unavailable) next.set(cacheKey, { url, reloadNonce });
      else next.delete(cacheKey);
      return next;
    });
  }, []);

  const upsertPreview = useCallback((descriptor: OperatingSystemPreviewDescriptor | null) => {
    if (!descriptor) return;
    rememberOperatingSystemPreviewSnapshot(descriptor);
    if (!descriptor.canEmbed) return;

    setEntries((currentEntries) => {
      const now = Date.now();
      const existingEntry = findExistingPreviewEntry(currentEntries, descriptor.cacheKey);
      const nextEntry = createNextCachedPreviewEntry(descriptor, existingEntry, now);
      const candidateEntry = createCandidateCachedPreviewEntry(descriptor, existingEntry, now);
      if (candidateEntry) {
        logRuntimePreviewCacheEvent({
          event: "os_runtime_preview_preserved_visible_frame",
          cacheKey: descriptor.cacheKey,
          osId: descriptor.osId,
          reason: "proxy_url_replaced",
          runtimeSource: descriptor.runtimeSource,
        });
      }
      const staleCandidateEntries = currentEntries.filter((entry) => (
        entry.candidateForCacheKey === descriptor.cacheKey &&
        !entry.loadedAt &&
        entry.cacheKey !== candidateEntry?.cacheKey
      ));
      const nextEntries = [
        ...(candidateEntry ? [candidateEntry] : []),
        nextEntry,
        ...currentEntries.filter((entry) => (
          entry.cacheKey !== descriptor.cacheKey &&
          entry.cacheKey !== candidateEntry?.cacheKey &&
          !isPreviewEntrySupersededByDescriptor(entry, descriptor) &&
          !staleCandidateEntries.some((stale) => stale.cacheKey === entry.cacheKey)
        )).map((entry) => refreshPreviewEntryDescriptorMetadata(entry, descriptor, now)),
      ];
      return prunePreviewEntries(
        nextEntries,
        activeKeyRef.current,
        keepAliveKeyRef.current,
      );
    });
  }, []);

  const prewarmPreview = useCallback((descriptor: OperatingSystemPreviewDescriptor | null) => {
    upsertPreview(descriptor);
  }, [upsertPreview]);

  const keepPreviewAlive = useCallback((descriptor: OperatingSystemPreviewDescriptor | null) => {
    if (!descriptor?.canEmbed) {
      setKeepAliveKey(null);
      keepAliveKeyRef.current = null;
      return;
    }
    setKeepAliveKey(descriptor.cacheKey);
    keepAliveKeyRef.current = descriptor.cacheKey;
    upsertPreview(descriptor);
  }, [upsertPreview]);

  const activatePreview = useCallback((descriptor: OperatingSystemPreviewDescriptor, target: HTMLElement) => {
    if (!descriptor.canEmbed) return () => undefined;
    const activationId = `${descriptor.cacheKey}:${Date.now()}:${Math.random()}`;
    const promotedEntry = entriesRef.current.find((entry) => (
      entry.candidateForCacheKey === descriptor.cacheKey && shouldRevealPreviewFrame(entry)
    ));
    const activationCacheKey = promotedEntry?.cacheKey ?? descriptor.cacheKey;
    activeKeyRef.current = activationCacheKey;
    upsertPreview(descriptor);
    setActivePreview({
      cacheKey: activationCacheKey,
      target,
      activationId,
    });

    return () => {
      setActivePreview((currentPreview) => (
        currentPreview?.activationId === activationId ? null : currentPreview
      ));
    };
  }, [upsertPreview]);

  const reloadPreview = useCallback((
    descriptor: OperatingSystemPreviewDescriptor | null,
    options: OperatingSystemPreviewReloadOptions = {},
  ) => {
    if (!descriptor) return;
    rememberOperatingSystemPreviewSnapshot(descriptor);
    if (!descriptor.canEmbed) return;
    setProviderLoadingKey(descriptor.cacheKey, false);
    const existingTimer = refreshTimersRef.current.get(descriptor.cacheKey);
    if (existingTimer) {
      window.clearTimeout(existingTimer);
      refreshTimersRef.current.delete(descriptor.cacheKey);
    }
    const existingLoadConfirmTimer = loadConfirmTimersRef.current.get(descriptor.cacheKey);
    if (existingLoadConfirmTimer) {
      window.clearTimeout(existingLoadConfirmTimer);
      loadConfirmTimersRef.current.delete(descriptor.cacheKey);
    }
    routeNotFoundCountsRef.current.delete(descriptor.cacheKey);
    runtimeErrorCountsRef.current.delete(descriptor.cacheKey);
    const runtimeErrorRetryTimer = runtimeErrorRetryTimersRef.current.get(descriptor.cacheKey);
    if (runtimeErrorRetryTimer !== undefined) {
      window.clearTimeout(runtimeErrorRetryTimer);
      runtimeErrorRetryTimersRef.current.delete(descriptor.cacheKey);
    }
    startupSeenAtRef.current.delete(descriptor.cacheKey);
    setEntries((currentEntries) => {
      const now = Date.now();
      const existingEntry = findExistingPreviewEntry(currentEntries, descriptor.cacheKey);
      if (shouldKeepInFlightPreview(existingEntry, descriptor.url, options.force)) {
        // A replacement with this URL is already loading. Keep its iframe alive.
        return currentEntries;
      }
      const preserveVisibleFrame = !options.force
        && !!existingEntry
        && shouldRevealPreviewFrame(existingEntry)
        && existingEntry.url !== descriptor.url;
      if (preserveVisibleFrame) {
        logRuntimePreviewCacheEvent({
          event: "os_runtime_preview_preserved_visible_frame",
          cacheKey: descriptor.cacheKey,
          osId: descriptor.osId,
          reason: "proxy_url_replaced",
          runtimeSource: descriptor.runtimeSource,
        });
      }
      const nextEntry: CachedPreviewEntry = {
        ...descriptor,
        createdAt: existingEntry?.createdAt ?? now,
        lastUsedAt: now,
        url: preserveVisibleFrame ? existingEntry.url : descriptor.url,
        embedUrl: preserveVisibleFrame ? existingEntry.embedUrl : descriptor.embedUrl,
        proxyExpiresAt: preserveVisibleFrame
          ? existingEntry.proxyExpiresAt
          : descriptor.proxyExpiresAt,
        loadedAt: preserveVisibleFrame ? existingEntry.loadedAt : null,
        reloadNonce: preserveVisibleFrame ? existingEntry.reloadNonce : (existingEntry?.reloadNonce ?? 0) + 1,
        lastSuccessfulSrcDoc: preserveVisibleFrame
          ? existingEntry.lastSuccessfulSrcDoc ?? null
          : null,
      };
      const candidateEntry = preserveVisibleFrame
        ? createCandidateCachedPreviewEntry(descriptor, existingEntry, now)
        : null;
      const staleCandidateEntries = currentEntries.filter((entry) => (
        entry.candidateForCacheKey === descriptor.cacheKey &&
        !entry.loadedAt &&
        entry.cacheKey !== candidateEntry?.cacheKey
      ));
      const nextActiveKey = options.force
        ? activePreviewCacheKeyAfterCandidatePromotion(
          currentEntries,
          activeKeyRef.current,
          descriptor.cacheKey,
          descriptor.cacheKey,
        )
        : activeKeyRef.current;
      return prunePreviewEntries(
        [
          ...(candidateEntry ? [candidateEntry] : []),
          nextEntry,
          ...currentEntries.filter((entry) => (
            entry.cacheKey !== descriptor.cacheKey &&
            entry.cacheKey !== candidateEntry?.cacheKey &&
            (!options.force || !previewEntryBelongsToCacheKey(entry, descriptor.cacheKey)) &&
            !isPreviewEntrySupersededByDescriptor(entry, descriptor) &&
            !staleCandidateEntries.some((stale) => stale.cacheKey === entry.cacheKey)
          )).map((entry) => refreshPreviewEntryDescriptorMetadata(entry, descriptor, now)),
        ],
        nextActiveKey,
        keepAliveKeyRef.current,
      );
    });
    if (options.force) {
      setActivePreview((currentPreview) => {
        if (!currentPreview) return currentPreview;
        const nextCacheKey = activePreviewCacheKeyAfterCandidatePromotion(
          entriesRef.current,
          currentPreview.cacheKey,
          descriptor.cacheKey,
          descriptor.cacheKey,
        );
        return nextCacheKey === currentPreview.cacheKey
          ? currentPreview
          : { ...currentPreview, cacheKey: nextCacheKey ?? currentPreview.cacheKey };
      });
    }
  }, [setProviderLoadingKey]);

  const isPreviewCached = useCallback((descriptor: OperatingSystemPreviewDescriptor | null) => (
    !!descriptor && entries.some((entry) => (
      entry.cacheKey === descriptor.cacheKey || entry.candidateForCacheKey === descriptor.cacheKey
    ))
  ), [entries]);

  const isPreviewLoaded = useCallback((descriptor: OperatingSystemPreviewDescriptor | null) => (
    !!descriptor && isActivePreviewEntryLoaded(
      entries,
      activePreview?.cacheKey ?? null,
      descriptor.cacheKey,
    )
  ), [activePreview?.cacheKey, entries]);

  const isPreviewFrameVisible = useCallback((descriptor: OperatingSystemPreviewDescriptor | null) => (
    !!descriptor && isActivePreviewFrameVisible(
      entries,
      activePreview?.cacheKey ?? null,
      descriptor.cacheKey,
      activeRect,
      !!activePreview?.target.isConnected,
      !runtimeBlankKeys.has(activePreview?.cacheKey ?? ""),
    )
  ), [activePreview, activeRect, entries, runtimeBlankKeys]);

  const isPreviewLoadedNow = useCallback((descriptor: OperatingSystemPreviewDescriptor | null) => (
    !!descriptor && entriesRef.current.some((entry) => (
      (entry.cacheKey === descriptor.cacheKey || entry.candidateForCacheKey === descriptor.cacheKey) &&
      shouldRevealPreviewFrame(entry)
    ))
  ), []);

  const isPreviewProviderLoading = useCallback((descriptor: OperatingSystemPreviewDescriptor | null) => (
    !!descriptor && providerLoadingKeys.has(descriptor.cacheKey)
  ), [providerLoadingKeys]);

  const isPreviewUnavailable = useCallback((descriptor: OperatingSystemPreviewDescriptor | null) => {
    if (!descriptor) return false;
    const key = activePreview?.cacheKey ?? descriptor.cacheKey;
    const entry = entries.find((candidate) => candidate.cacheKey === key && previewEntryBelongsToCacheKey(candidate, descriptor.cacheKey));
    const failure = unavailableFrames.get(key);
    return !!entry && !!failure && entry.url === failure.url && entry.reloadNonce === failure.reloadNonce;
  }, [activePreview?.cacheKey, entries, unavailableFrames]);

  const getPreviewSnapshot = useCallback((descriptor: OperatingSystemPreviewDescriptor | null) => (
    getStoredOperatingSystemPreviewSnapshot(descriptor)
  ), []);

  const rejectCandidatePreview = useCallback((
    entry: CachedPreviewEntry,
    reason: "provider_loading" | "route_not_found" | "runtime_error",
    phase?: OperatingSystemRuntimeLifecycleClientPhase | null,
  ) => {
    const parentKey = entry.candidateForCacheKey;
    if (!parentKey || !isPendingPreviewCandidate(entry)) return;
    reportRuntimeLifecycleEvent(entry, "candidate_rejected", reason, phase);
    logRuntimePreviewCacheEvent({
      event: "candidate_runtime_rejected",
      cacheKey: parentKey,
      osId: entry.osId,
      reason,
      runtimeSource: entry.runtimeSource,
    });
    setProviderLoadingKey(entry.cacheKey, false);
    setEntries((currentEntries) => currentEntries.filter((candidate) => candidate.cacheKey !== entry.cacheKey));
    iframeRefsRef.current.delete(entry.cacheKey);
  }, [reportRuntimeLifecycleEvent, setProviderLoadingKey]);

  const promoteCandidatePreview = useCallback((entry: CachedPreviewEntry) => {
    const parentKey = entry.candidateForCacheKey;
    if (!parentKey || !shouldPromotePreviewCandidate(entry, true)) return false;

    const wasLoaded = entriesRef.current.some((candidate) => (
      previewEntryBelongsToCacheKey(candidate, parentKey) &&
      shouldRevealPreviewFrame(candidate)
    ));
    const now = Date.now();
    logRuntimePreviewCacheEvent({
      event: "candidate_runtime_promoted",
      cacheKey: parentKey,
      osId: entry.osId,
      reason: "runtime_ready",
      runtimeSource: entry.runtimeSource,
    });
    reportRuntimeLifecycleEvent(entry, "runtime_loaded", "runtime_ready");
    setProviderLoadingKey(entry.cacheKey, false);
    setProviderLoadingKey(parentKey, false);
    setEntries((currentEntries) => {
      const parentEntry = currentEntries.find((candidate) => candidate.cacheKey === parentKey);
      const promotedEntry: CachedPreviewEntry = {
        ...entry,
        loadedAt: now,
        lastUsedAt: now,
      };
      runtimeHeartbeatAtRef.current.set(promotedEntry.cacheKey, now);
      runtimeHeartbeatRecoveryCountsRef.current.delete(promotedEntry.cacheKey);
      const parentMarker: CachedPreviewEntry | null = parentEntry
        ? {
          ...parentEntry,
          url: entry.url,
          embedUrl: entry.embedUrl,
          proxyExpiresAt: entry.proxyExpiresAt,
          screenshotUrl: entry.screenshotUrl,
          loadedAt: now,
          lastUsedAt: now,
          lastSuccessfulSrcDoc: entry.lastSuccessfulSrcDoc ?? null,
        }
        : null;
      const filteredEntries = currentEntries.filter((candidate) => (
        candidate.cacheKey !== entry.cacheKey &&
        candidate.cacheKey !== parentKey &&
        candidate.candidateForCacheKey !== parentKey
      ));
      const nextActiveKey = activePreviewCacheKeyAfterCandidatePromotion(
        currentEntries,
        activeKeyRef.current,
        parentKey,
        promotedEntry.cacheKey,
      );
      return prunePreviewEntries(
        [promotedEntry, ...(parentMarker ? [parentMarker] : []), ...filteredEntries],
        nextActiveKey,
        keepAliveKeyRef.current,
      );
    });
    setActivePreview((currentPreview) => {
      if (!currentPreview) return currentPreview;
      const nextCacheKey = activePreviewCacheKeyAfterCandidatePromotion(
        entriesRef.current,
        currentPreview.cacheKey,
        parentKey,
        entry.cacheKey,
      );
      return nextCacheKey === currentPreview.cacheKey
        ? currentPreview
        : { ...currentPreview, cacheKey: nextCacheKey ?? currentPreview.cacheKey };
    });
    rememberOperatingSystemPreviewSnapshot({ ...entry, cacheKey: parentKey });
    if (!wasLoaded) {
      const detail: OperatingSystemPreviewLoadedDetail = {
        cacheKey: parentKey,
        osId: entry.osId,
      };
      window.dispatchEvent(new CustomEvent(OPERATING_SYSTEM_PREVIEW_LOADED_EVENT, { detail }));
    }
    return true;
  }, [reportRuntimeLifecycleEvent, setProviderLoadingKey]);

  const markPreviewLoaded = useCallback((descriptor: CachedPreviewEntry) => {
    const existingLoadConfirmTimer = loadConfirmTimersRef.current.get(descriptor.cacheKey);
    if (existingLoadConfirmTimer) {
      window.clearTimeout(existingLoadConfirmTimer);
      loadConfirmTimersRef.current.delete(descriptor.cacheKey);
    }

    const expectedIframe = iframeRefsRef.current.get(descriptor.cacheKey);
    if (!expectedIframe) return;

    const timer = window.setTimeout(() => {
      loadConfirmTimersRef.current.delete(descriptor.cacheKey);

      try {
        const iframe = iframeRefsRef.current.get(descriptor.cacheKey);
        const currentEntry = entriesRef.current.find((entry) => entry.cacheKey === descriptor.cacheKey);
        if (iframe !== expectedIframe || !currentEntry ||
          currentEntry.reloadNonce !== descriptor.reloadNonce || currentEntry.url !== descriptor.url ||
          providerLoadingKeysRef.current.has(descriptor.cacheKey) ||
          providerLoadingKeysRef.current.has(canonicalPreviewCacheKey(currentEntry))) return;
      } catch {
        return;
      }

      const existingTimer = refreshTimersRef.current.get(descriptor.cacheKey);
      if (existingTimer) {
        window.clearTimeout(existingTimer);
        refreshTimersRef.current.delete(descriptor.cacheKey);
      }
      const wasLoaded = entriesRef.current.some((entry) => (
        entry.cacheKey === descriptor.cacheKey && shouldRevealPreviewFrame(entry)
      ));
      startupSeenAtRef.current.delete(descriptor.cacheKey);
      runtimeErrorCountsRef.current.delete(descriptor.cacheKey);
      const pendingRuntimeErrorRetry = runtimeErrorRetryTimersRef.current.get(descriptor.cacheKey);
      if (pendingRuntimeErrorRetry !== undefined) {
        window.clearTimeout(pendingRuntimeErrorRetry);
        runtimeErrorRetryTimersRef.current.delete(descriptor.cacheKey);
      }
      runtimeHeartbeatAtRef.current.set(descriptor.cacheKey, Date.now());
      runtimeHeartbeatRecoveryCountsRef.current.delete(descriptor.cacheKey);
      setProviderLoadingKey(descriptor.cacheKey, false);
      reportRuntimeLifecycleEvent(descriptor, "runtime_loaded", "runtime_ready");
      rememberOperatingSystemPreviewSnapshot(descriptor);
      setEntries((currentEntries) => currentEntries.map((entry) => (
        entry.cacheKey === descriptor.cacheKey
          ? { ...entry, loadedAt: Date.now(), lastUsedAt: Date.now(), screenshotUrl: descriptor.screenshotUrl }
          : entry
      )));
      if (!wasLoaded) {
        const detail: OperatingSystemPreviewLoadedDetail = {
          cacheKey: descriptor.cacheKey,
          osId: descriptor.osId,
        };
        window.dispatchEvent(new CustomEvent(OPERATING_SYSTEM_PREVIEW_LOADED_EVENT, { detail }));
      }
    }, PREVIEW_LOAD_CONFIRM_DELAY_MS);
    loadConfirmTimersRef.current.set(descriptor.cacheKey, timer);
  }, [reportRuntimeLifecycleEvent, setProviderLoadingKey]);

  const notifyProviderLoading = useCallback((cacheKey: string) => {
    const entry = entriesRef.current.find((candidate) => candidate.cacheKey === cacheKey);
    if (!entry) return;
    if (isPendingPreviewCandidate(entry)) return;
    const detail: OperatingSystemPreviewProviderLoadingDetail = {
      cacheKey: canonicalPreviewCacheKey(entry),
      osId: entry.osId,
    };
    window.dispatchEvent(new CustomEvent(OPERATING_SYSTEM_PREVIEW_PROVIDER_LOADING_EVENT, { detail }));
  }, []);

  const recoverPreviewAfterLostContent = useCallback((
    entry: CachedPreviewEntry,
    reason: "runtime_heartbeat_blank" | "runtime_heartbeat_timeout",
  ) => {
    if (!entry.loadedAt || !shouldRevealPreviewFrame(entry)) return false;
    allowRecoveredReadyEvent(entry);

    const retryCount = runtimeHeartbeatRecoveryCountsRef.current.get(entry.cacheKey) ?? 0;
    const now = Date.now();
    runtimeHeartbeatAtRef.current.set(entry.cacheKey, now);
    runtimeHeartbeatTimeoutCountsRef.current.delete(entry.cacheKey);
    if (retryCount >= PREVIEW_RUNTIME_ERROR_MAX_RELOADS) return false;
    runtimeHeartbeatRecoveryCountsRef.current.set(entry.cacheKey, retryCount + 1);
    logRuntimePreviewCacheEvent({
      event: "runtime_heartbeat_recovery",
      cacheKey: canonicalPreviewCacheKey(entry),
      osId: entry.osId,
      reason,
      runtimeSource: entry.runtimeSource,
    });
    setEntries((currentEntries) => currentEntries.map((currentEntry) => (
      currentEntry.cacheKey === entry.cacheKey
        ? recoverPreviewEntryAfterHeartbeat(
          currentEntry,
          retryCount,
          PREVIEW_RUNTIME_ERROR_MAX_RELOADS,
          now,
        )
        : currentEntry
    )));
    return true;
  }, [allowRecoveredReadyEvent]);

  const handleProviderLoadingState = useCallback((cacheKey: string, loading: boolean) => {
    const entry = entriesRef.current.find((candidate) => candidate.cacheKey === cacheKey);
    if (loading && entry && isPendingPreviewCandidate(entry)) {
      rejectCandidatePreview(entry, "provider_loading");
      return;
    }

    const canonicalCacheKey = entry ? canonicalPreviewCacheKey(entry) : cacheKey;

    if (loading) {
      setProviderLoadingKey(canonicalCacheKey, true);
      // The iframe already refetches root HTML and startup assets on this ticket.
      // A full preview refresh would rotate the ticket during that warm-up.
      const existingLoadConfirmTimer = loadConfirmTimersRef.current.get(cacheKey);
      if (existingLoadConfirmTimer) {
        window.clearTimeout(existingLoadConfirmTimer);
        loadConfirmTimersRef.current.delete(cacheKey);
      }
      setEntries((currentEntries) => currentEntries.map((entry) => (
        entry.cacheKey === cacheKey
          ? { ...entry, loadedAt: entry.loadedAt, lastUsedAt: Date.now() }
          : entry
      )));
      return;
    }

    setProviderLoadingKey(cacheKey, false);
    if (canonicalCacheKey !== cacheKey) setProviderLoadingKey(canonicalCacheKey, false);
  }, [rejectCandidatePreview, setProviderLoadingKey]);

  const handleRuntimeHeartbeatMessage = useCallback((event: MessageEvent) => {
    const data = event.data as { type?: unknown; visible?: unknown } | null;
    if (!data || typeof data !== "object" || data.type !== RUNTIME_HEARTBEAT_MESSAGE_TYPE) return;
    const entry = entriesRef.current.find((candidate) => {
      const iframe = iframeRefsRef.current.get(candidate.cacheKey);
      return !!iframe && event.source === iframe.contentWindow;
    });
    if (!entry) return;

    const now = Date.now();
    runtimeHeartbeatAtRef.current.set(entry.cacheKey, now);
    runtimeHeartbeatTimeoutCountsRef.current.delete(entry.cacheKey);
    if (data.visible !== false) {
      setRuntimeBlankKeys((current) => {
        if (!current.has(entry.cacheKey)) return current;
        const next = new Set(current);
        next.delete(entry.cacheKey);
        return next;
      });
      runtimeHeartbeatBlankCountsRef.current.delete(entry.cacheKey);
      runtimeHeartbeatBlankSinceRef.current.delete(entry.cacheKey);
      runtimeHeartbeatRecoveryCountsRef.current.delete(entry.cacheKey);
      return;
    }
    setRuntimeBlankKeys((current) => current.has(entry.cacheKey) ? current : new Set(current).add(entry.cacheKey));
    if (isPendingPreviewCandidate(entry)) {
      rejectCandidatePreview(entry, "runtime_error");
      return;
    }

    const blankCount = (runtimeHeartbeatBlankCountsRef.current.get(entry.cacheKey) ?? 0) + 1;
    const blankSince = runtimeHeartbeatBlankSinceRef.current.get(entry.cacheKey) ?? now;
    runtimeHeartbeatBlankCountsRef.current.set(entry.cacheKey, blankCount);
    runtimeHeartbeatBlankSinceRef.current.set(entry.cacheKey, blankSince);
    if (!shouldRecoverAfterRuntimeHeartbeatBlank(
      blankCount,
      blankSince,
      now,
      RUNTIME_HEARTBEAT_BLANK_SAMPLES,
      RUNTIME_HEARTBEAT_BLANK_GRACE_MS,
    )) {
      return;
    }
    runtimeHeartbeatBlankCountsRef.current.delete(entry.cacheKey);
    runtimeHeartbeatBlankSinceRef.current.delete(entry.cacheKey);
    recoverPreviewAfterLostContent(entry, "runtime_heartbeat_blank");
  }, [recoverPreviewAfterLostContent, rejectCandidatePreview]);

  const handleProviderStartupMessage = useCallback((event: MessageEvent) => {
    const data = event.data as { type?: unknown } | null;
    if (!data || typeof data !== "object" || data.type !== "preview:requestRefresh") return;
    const entry = entriesRef.current.find((candidate) => {
      const iframe = iframeRefsRef.current.get(candidate.cacheKey);
      return !!iframe && event.source === iframe.contentWindow;
    });
    if (!entry) return;
    if (isPendingPreviewCandidate(entry)) {
      rejectCandidatePreview(entry, "provider_loading");
      return;
    }

    const now = Date.now();
    setProviderLoadingKey(canonicalPreviewCacheKey(entry), true);
    notifyProviderLoading(entry.cacheKey);
    startupSeenAtRef.current.set(entry.cacheKey, now);
    const existingLoadConfirmTimer = loadConfirmTimersRef.current.get(entry.cacheKey);
    if (existingLoadConfirmTimer) {
      window.clearTimeout(existingLoadConfirmTimer);
      loadConfirmTimersRef.current.delete(entry.cacheKey);
    }
    // The iframe owns a five-second HTML refetch while the Provider is waking.
    // Remounting it here discards an in-flight startup and its first paint.

    if (entry.loadedAt) {
      logRuntimePreviewCacheEvent({
        event: "os_runtime_preview_preserved_visible_frame",
        cacheKey: canonicalPreviewCacheKey(entry),
        osId: entry.osId,
        reason: "provider_loading_after_loaded",
        runtimeSource: entry.runtimeSource,
      });
    }

    setEntries((currentEntries) => currentEntries.map((currentEntry) => (
      currentEntry.cacheKey === entry.cacheKey
        ? { ...currentEntry, loadedAt: currentEntry.loadedAt, lastUsedAt: now }
        : currentEntry
    )));
  }, [notifyProviderLoading, rejectCandidatePreview, setProviderLoadingKey]);

  const handleRuntimeReadyMessage = useCallback((event: MessageEvent) => {
    const data = event.data as { type?: unknown } | null;
    if (!data || typeof data !== "object" || data.type !== RUNTIME_READY_MESSAGE_TYPE) return;
    const entry = entriesRef.current.find((candidate) => {
      const iframe = iframeRefsRef.current.get(candidate.cacheKey);
      return !!iframe && event.source === iframe.contentWindow;
    });
    if (!entry) return;
    if (isPendingPreviewCandidate(entry)) return;
    routeNotFoundCountsRef.current.delete(entry.cacheKey);
    // A ready signal from this exact iframe supersedes its earlier loading or
    // error state. Otherwise the confirmation gate rejects ready forever.
    const pendingErrorRetry = runtimeErrorRetryTimersRef.current.get(entry.cacheKey);
    if (pendingErrorRetry !== undefined) {
      window.clearTimeout(pendingErrorRetry);
      runtimeErrorRetryTimersRef.current.delete(entry.cacheKey);
    }
    setProviderLoadingKey(entry.cacheKey, false);
    const canonicalKey = canonicalPreviewCacheKey(entry);
    if (canonicalKey !== entry.cacheKey) setProviderLoadingKey(canonicalKey, false);
    markPreviewLoaded(entry);
  }, [markPreviewLoaded, setProviderLoadingKey]);

  const handleRuntimeStableMessage = useCallback((event: MessageEvent) => {
    const data = event.data as { type?: unknown } | null;
    if (!data || typeof data !== "object" || data.type !== RUNTIME_STABLE_MESSAGE_TYPE) return;
    const entry = entriesRef.current.find((candidate) => {
      const iframe = iframeRefsRef.current.get(candidate.cacheKey);
      return !!iframe && event.source === iframe.contentWindow;
    });
    if (entry && shouldPromotePreviewCandidate(entry, true)) promoteCandidatePreview(entry);
  }, [promoteCandidatePreview]);

  const handleRuntimeRouteNotFoundMessage = useCallback((event: MessageEvent) => {
    const data = event.data as { type?: unknown } | null;
    if (!data || typeof data !== "object" || data.type !== RUNTIME_ROUTE_NOT_FOUND_MESSAGE_TYPE) return;
    const entry = entriesRef.current.find((candidate) => {
      const iframe = iframeRefsRef.current.get(candidate.cacheKey);
      return !!iframe && event.source === iframe.contentWindow;
    });
    if (!entry) return;
    if (isPendingPreviewCandidate(entry)) {
      rejectCandidatePreview(entry, "route_not_found");
      return;
    }

    const count = routeNotFoundCountsRef.current.get(entry.cacheKey) ?? 0;
    routeNotFoundCountsRef.current.set(entry.cacheKey, count + 1);
    setProviderLoadingKey(canonicalPreviewCacheKey(entry), true);
    notifyProviderLoading(entry.cacheKey);

    const existingLoadConfirmTimer = loadConfirmTimersRef.current.get(entry.cacheKey);
    if (existingLoadConfirmTimer) {
      window.clearTimeout(existingLoadConfirmTimer);
      loadConfirmTimersRef.current.delete(entry.cacheKey);
    }
    const existingTimer = refreshTimersRef.current.get(entry.cacheKey);
    if (existingTimer) {
      window.clearTimeout(existingTimer);
      refreshTimersRef.current.delete(entry.cacheKey);
    }

    setEntries((currentEntries) => currentEntries.map((currentEntry) => {
      if (currentEntry.cacheKey !== entry.cacheKey) return currentEntry;
      if (currentEntry.loadedAt) {
        logRuntimePreviewCacheEvent({
          event: "os_runtime_preview_preserved_visible_frame",
          cacheKey: currentEntry.cacheKey,
          osId: currentEntry.osId,
          reason: "route_not_found_after_loaded",
          runtimeSource: currentEntry.runtimeSource,
        });
        return {
          ...currentEntry,
          lastUsedAt: Date.now(),
        };
      }
      return {
        ...currentEntry,
        loadedAt: null,
        lastUsedAt: Date.now(),
        reloadNonce: count < PREVIEW_ROUTE_NOT_FOUND_MAX_RELOADS
          ? currentEntry.reloadNonce + 1
          : currentEntry.reloadNonce,
      };
    }));
  }, [notifyProviderLoading, rejectCandidatePreview, setProviderLoadingKey]);

  const handleRuntimeErrorMessage = useCallback((event: MessageEvent) => {
    const data = event.data as {
      type?: unknown; phase?: unknown; resourceType?: unknown; resourceExtension?: unknown;
      sameOrigin?: unknown; errorName?: unknown; line?: unknown; column?: unknown;
    } | null;
    if (!data || typeof data !== "object" || data.type !== RUNTIME_ERROR_MESSAGE_TYPE) return;
    const entry = entriesRef.current.find((candidate) => {
      const iframe = iframeRefsRef.current.get(candidate.cacheKey);
      return !!iframe && event.source === iframe.contentWindow;
    });
    if (!entry) return;
    const phase = normalizeRuntimeErrorPhase(data.phase, entry.loadedAt ? "after_ready" : "before_ready");
    logRuntimePreviewCacheEvent({
      event: "runtime_preview_error_detected",
      cacheKey: entry.cacheKey,
      osId: entry.osId,
      reason: entry.loadedAt ? "runtime_error_after_loaded" : "runtime_error",
      runtimeSource: entry.runtimeSource,
      errorPhase: phase,
      resourceType: data.resourceType === "script" || data.resourceType === "link" ? data.resourceType : undefined,
      resourceExtension: typeof data.resourceExtension === "string" && /^[a-z0-9]{1,8}$/.test(data.resourceExtension) ? data.resourceExtension : undefined,
      sameOrigin: typeof data.sameOrigin === "boolean" ? data.sameOrigin : null,
      errorName: typeof data.errorName === "string" && /^[A-Za-z]{1,40}$/.test(data.errorName) ? data.errorName : undefined,
      line: Number.isSafeInteger(data.line) && Number(data.line) >= 0 ? Number(data.line) : undefined,
      column: Number.isSafeInteger(data.column) && Number(data.column) >= 0 ? Number(data.column) : undefined,
    });
    if (isPendingPreviewCandidate(entry)) {
      rejectCandidatePreview(entry, "runtime_error", phase);
      return;
    }
    allowRecoveredReadyEvent(entry);

    if (runtimeErrorRetryTimersRef.current.has(entry.cacheKey)) return;
    const count = runtimeErrorCountsRef.current.get(entry.cacheKey) ?? 0;
    const retryDelay = runtimeStartupErrorRetryDelay(count);
    runtimeErrorCountsRef.current.set(entry.cacheKey, count + 1);
    setProviderLoadingKey(canonicalPreviewCacheKey(entry), true);
    reportRuntimeLifecycleEvent(entry, "runtime_error", "runtime_error", phase);

    const existingLoadConfirmTimer = loadConfirmTimersRef.current.get(entry.cacheKey);
    if (existingLoadConfirmTimer) {
      window.clearTimeout(existingLoadConfirmTimer);
      loadConfirmTimersRef.current.delete(entry.cacheKey);
    }
    const existingTimer = refreshTimersRef.current.get(entry.cacheKey);
    if (existingTimer) {
      window.clearTimeout(existingTimer);
      refreshTimersRef.current.delete(entry.cacheKey);
    }

    setEntries((currentEntries) => currentEntries.map((currentEntry) => {
      if (currentEntry.cacheKey !== entry.cacheKey) return currentEntry;
      if (currentEntry.loadedAt) {
        logRuntimePreviewCacheEvent({
          event: "runtime_preview_hidden_after_error",
          cacheKey: canonicalPreviewCacheKey(currentEntry),
          osId: currentEntry.osId,
          reason: "runtime_error_after_loaded",
          runtimeSource: currentEntry.runtimeSource,
        });
      }
      return hidePreviewEntryAfterRuntimeError(currentEntry);
    }));
    if (retryDelay !== null) {
      const failedNonce = entry.reloadNonce;
      const timer = window.setTimeout(() => {
        runtimeErrorRetryTimersRef.current.delete(entry.cacheKey);
        setEntries((currentEntries) => currentEntries.map((currentEntry) => (
          currentEntry.cacheKey === entry.cacheKey &&
          currentEntry.reloadNonce === failedNonce &&
          !currentEntry.loadedAt
            ? recoverPreviewEntryAfterRuntimeError(currentEntry, 0, 1)
            : currentEntry
        )));
      }, retryDelay);
      runtimeErrorRetryTimersRef.current.set(entry.cacheKey, timer);
    }
  }, [allowRecoveredReadyEvent, rejectCandidatePreview, reportRuntimeLifecycleEvent, setProviderLoadingKey]);

  const handleRuntimeStorageMessage = useCallback((event: MessageEvent) => {
    const isNavigation = event.data?.type === RUNTIME_NAVIGATION_MESSAGE;
    const payload = parseOperatingSystemRuntimeStorageMessage(event.data);
    if (!payload && !isNavigation) return;
    const entry = entriesRef.current.find((candidate) => {
      const iframe = iframeRefsRef.current.get(candidate.cacheKey);
      return !!iframe && event.source === iframe.contentWindow;
    });
    if (!entry) return;
    if (isNavigation) persistRuntimeNavigation(entry.osId, event.data.snapshot);
    else if (payload) persistOperatingSystemRuntimeStorage(entry.osId, payload.name, payload.values);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      const activeCacheKey = activeKeyRef.current;
      if (!activeCacheKey) return;
      const activeEntry = entriesRef.current.find((entry) => entry.cacheKey === activeCacheKey);
      if (!activeEntry || !shouldRevealPreviewFrame(activeEntry)) return;

      const now = Date.now();
      if (typeof document !== "undefined" && document.visibilityState !== "visible") {
        runtimeHeartbeatAtRef.current.set(activeCacheKey, now);
        runtimeHeartbeatBlankCountsRef.current.delete(activeCacheKey);
        runtimeHeartbeatBlankSinceRef.current.delete(activeCacheKey);
        runtimeHeartbeatTimeoutCountsRef.current.delete(activeCacheKey);
        return;
      }
      const lastHeartbeatAt = runtimeHeartbeatAtRef.current.get(activeCacheKey);
      if (!lastHeartbeatAt) {
        runtimeHeartbeatAtRef.current.set(activeCacheKey, now);
        return;
      }
      if (now - lastHeartbeatAt < RUNTIME_HEARTBEAT_TIMEOUT_MS) {
        runtimeHeartbeatTimeoutCountsRef.current.delete(activeCacheKey);
        return;
      }
      const missedTimeouts = (runtimeHeartbeatTimeoutCountsRef.current.get(activeCacheKey) ?? 0) + 1;
      runtimeHeartbeatTimeoutCountsRef.current.set(activeCacheKey, missedTimeouts);
      if (missedTimeouts < RUNTIME_HEARTBEAT_TIMEOUT_MISSES) return;
      runtimeHeartbeatTimeoutCountsRef.current.delete(activeCacheKey);
      recoverPreviewAfterLostContent(activeEntry, "runtime_heartbeat_timeout");
    }, RUNTIME_HEARTBEAT_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [recoverPreviewAfterLostContent]);

  useEffect(() => {
    activeKeyRef.current = activePreview?.cacheKey ?? null;
    setEntries((currentEntries) => prunePreviewEntries(currentEntries, activePreview?.cacheKey ?? null, keepAliveKeyRef.current));
  }, [activePreview?.cacheKey]);

  useEffect(() => {
    keepAliveKeyRef.current = keepAliveKey;
    setEntries((currentEntries) => prunePreviewEntries(currentEntries, activeKeyRef.current, keepAliveKey));
  }, [keepAliveKey]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setEntries((currentEntries) => prunePreviewEntries(currentEntries, activeKeyRef.current, keepAliveKeyRef.current));
    }, PRUNE_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const refreshTimers = refreshTimersRef.current;
    const loadConfirmTimers = loadConfirmTimersRef.current;
    const providerLoadingKeys = providerLoadingKeysRef.current;
    const routeNotFoundCounts = routeNotFoundCountsRef.current;
    const runtimeErrorCounts = runtimeErrorCountsRef.current;
    const runtimeErrorRetryTimers = runtimeErrorRetryTimersRef.current;
    const runtimeHeartbeatAt = runtimeHeartbeatAtRef.current;
    const runtimeHeartbeatRecoveryCounts = runtimeHeartbeatRecoveryCountsRef.current;
    const runtimeHeartbeatBlankCounts = runtimeHeartbeatBlankCountsRef.current;
    const runtimeHeartbeatBlankSince = runtimeHeartbeatBlankSinceRef.current;
    const runtimeHeartbeatTimeoutCounts = runtimeHeartbeatTimeoutCountsRef.current;
    const iframeRefs = iframeRefsRef.current;
    window.addEventListener("message", handleProviderStartupMessage);
    window.addEventListener("message", handleRuntimeReadyMessage);
    window.addEventListener("message", handleRuntimeStableMessage);
    window.addEventListener("message", handleRuntimeRouteNotFoundMessage);
    window.addEventListener("message", handleRuntimeErrorMessage);
    window.addEventListener("message", handleRuntimeHeartbeatMessage);
    window.addEventListener("message", handleRuntimeStorageMessage);
    return () => {
      window.removeEventListener("message", handleProviderStartupMessage);
      window.removeEventListener("message", handleRuntimeReadyMessage);
      window.removeEventListener("message", handleRuntimeStableMessage);
      window.removeEventListener("message", handleRuntimeRouteNotFoundMessage);
      window.removeEventListener("message", handleRuntimeErrorMessage);
      window.removeEventListener("message", handleRuntimeHeartbeatMessage);
      window.removeEventListener("message", handleRuntimeStorageMessage);
      for (const timer of refreshTimers.values()) {
        window.clearTimeout(timer);
      }
      refreshTimers.clear();
      for (const timer of loadConfirmTimers.values()) {
        window.clearTimeout(timer);
      }
      loadConfirmTimers.clear();
      providerLoadingKeys.clear();
      routeNotFoundCounts.clear();
      runtimeErrorCounts.clear();
      for (const timer of runtimeErrorRetryTimers.values()) window.clearTimeout(timer);
      runtimeErrorRetryTimers.clear();
      runtimeHeartbeatAt.clear();
      runtimeHeartbeatRecoveryCounts.clear();
      runtimeHeartbeatBlankCounts.clear();
      runtimeHeartbeatBlankSince.clear();
      runtimeHeartbeatTimeoutCounts.clear();
      iframeRefs.clear();
    };
  }, [handleProviderStartupMessage, handleRuntimeReadyMessage, handleRuntimeStableMessage, handleRuntimeRouteNotFoundMessage, handleRuntimeErrorMessage, handleRuntimeHeartbeatMessage, handleRuntimeStorageMessage]);

  useLayoutEffect(() => {
    if (!activePreview) {
      setActiveRect(null);
      return undefined;
    }

    let animationFrame = 0;
    const measure = () => {
      const rect = activePreview.target.getBoundingClientRect();
      setActiveRect({
        left: rect.left,
        top: rect.top,
        width: Math.max(0, rect.width),
        height: Math.max(0, rect.height),
      });
    };
    const scheduleMeasure = () => {
      if (animationFrame) return;
      animationFrame = window.requestAnimationFrame(() => {
        animationFrame = 0;
        measure();
      });
    };

    measure();
    const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(scheduleMeasure) : null;
    resizeObserver?.observe(activePreview.target);
    window.addEventListener("resize", scheduleMeasure);
    window.addEventListener("scroll", scheduleMeasure, true);

    return () => {
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", scheduleMeasure);
      window.removeEventListener("scroll", scheduleMeasure, true);
    };
  }, [activePreview]);

  const contextValue = useMemo<OperatingSystemPreviewCacheValue>(() => ({
    activatePreview,
    prewarmPreview,
    keepPreviewAlive,
    reloadPreview,
    isPreviewCached,
    isPreviewLoaded,
    isPreviewFrameVisible,
    isPreviewLoadedNow,
    isPreviewProviderLoading,
    isPreviewUnavailable,
    getPreviewSnapshot,
  }), [
    activatePreview,
    getPreviewSnapshot,
    isPreviewCached,
    isPreviewLoaded,
    isPreviewFrameVisible,
    isPreviewLoadedNow,
    keepPreviewAlive,
    prewarmPreview,
    reloadPreview,
    isPreviewProviderLoading,
    isPreviewUnavailable,
  ]);

  return (
    <OperatingSystemPreviewCacheContext.Provider value={contextValue}>
      {children}
      {entries.filter((entry) => shouldRenderPreviewFrame(entry, entries)).map((entry) => {
        const selected = activePreview?.cacheKey === entry.cacheKey;
        const active = selected
          && !!activeRect
          && activeRect.width > 0
          && activeRect.height > 0
          && shouldRevealPreviewFrame(entry);
        return (
          <RuntimePreviewFrame
            key={`${entry.cacheKey}:${entry.reloadNonce}`}
            entry={entry}
            active={active}
            activeRect={selected ? activeRect : null}
            onFrameRef={(cacheKey, node) => {
              if (node) {
                iframeRefsRef.current.set(cacheKey, node);
              } else {
                iframeRefsRef.current.delete(cacheKey);
              }
            }}
            onProviderLoadingState={handleProviderLoadingState}
            onNormalizedSrcDoc={rememberNormalizedSrcDoc}
            onRuntimeUnavailable={handleRuntimeUnavailable}
          />
        );
      })}
    </OperatingSystemPreviewCacheContext.Provider>
  );
}
